import {
  createContext,
  useContext,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { debounce } from 'lodash';
import { useRouter } from 'next/router';
import { useMount } from 'react-use';
import { getUserDetails, slugify } from '@surfline/web-common';

import usePreferredForecastView from 'hooks/usePreferredForecastView';
import { useUserPreferredForecastView } from 'selectors/user';
import { useAppSelector } from 'stores/hooks';
import { spotReportPath } from 'utils/urls';

interface URLStateContextType {
  updateViewPreference: (params: {
    isTableView: boolean;
    spotId?: string;
    spotName?: string;
    isWavePool?: boolean;
  }) => void;
  updateCameraId: (params: {
    camId?: string;
    spotId?: string;
    spotName?: string;
    isWavePool?: boolean;
  }) => void;
  readonly isHydrated: boolean;
}

const URLStateContext = createContext<URLStateContextType | null>(null);

export const useURLState = (): URLStateContextType => {
  const context = useContext(URLStateContext);
  if (!context) {
    throw new Error('useURLState must be used within a URLStateProvider');
  }
  return context;
};

interface URLStateProviderProps {
  children: React.ReactNode;
  spotId?: string;
  spotName?: string;
  isWavePool?: boolean;
}

interface UpdateURLParams {
  isTableView?: boolean;
  camId?: string;
  updateCamId?: boolean;
  spotId?: string;
  spotName?: string;
  isWavePool?: boolean;
  isCustomForecast?: boolean;
}

type QueryObject = Record<string, string | string[] | undefined>;

const updateCamIdInQuery = (query: QueryObject, params: UpdateURLParams): QueryObject => {
  const updatedQuery = { ...query };
  if (params.camId !== undefined || params.updateCamId) {
    if (params.camId) {
      updatedQuery.camId = params.camId;
    } else {
      delete updatedQuery.camId;
    }
  }
  return updatedQuery;
};

// Cached URL pattern builders to avoid repeated work
const URL_BUILDERS = {
  customForecast: (query: QueryObject, asPath: string, params: UpdateURLParams): string => {
    const newQuery = { ...query };

    delete newQuery.location;
    delete newQuery.customFavoriteId;

    if (params.isTableView) {
      newQuery.view = 'table';
    } else {
      delete newQuery.view;
    }

    return `${asPath?.split('?')[0]}?${new URLSearchParams(
      newQuery as Record<string, string>,
    ).toString()}`;
  },

  rateTheSurf: (query: QueryObject, params: UpdateURLParams): string => {
    if (!params.spotId || !params.spotName) {
      throw new Error('spotId and spotName are required for rate-the-surf URLs');
    }

    const newQuery = { ...query };

    // Strip contextual routing internals and any spot-id/slug remnants
    delete (newQuery as any).slug;
    delete (newQuery as any).id;
    delete (newQuery as any).rateTheSurf;
    Object.keys(newQuery).forEach((key) => {
      if (key.startsWith('_UCR_')) delete (newQuery as any)[key];
    });

    // While the rate-the-surf modal is open, keep the visible URL clean
    // Remove volatile spot query params like camId/view so the masked path stays stable
    delete (newQuery as any).camId;
    delete (newQuery as any).view;

    const finalQuery = newQuery as Record<string, string>;

    const base = '/surf-report';
    const basePath = `${base}/${slugify(params.spotName)}/${params.spotId}/rate-the-surf`;
    const queryString = new URLSearchParams(finalQuery).toString();
    return `${basePath}${queryString ? `?${queryString}` : ''}`;
  },

  wavePool: (query: QueryObject, params: UpdateURLParams): string => {
    if (!params.spotId || !params.spotName) {
      throw new Error('spotId and spotName are required for wave pool URLs');
    }

    const newQuery = { ...query };

    delete newQuery.id;
    delete newQuery.slug;
    delete newQuery.view;

    const finalQuery = updateCamIdInQuery(newQuery, params);

    return spotReportPath(finalQuery, {
      _id: params.spotId,
      name: params.spotName,
      isWavePool: true,
    });
  },

  regular: (query: QueryObject, params: UpdateURLParams): string => {
    if (!params.spotId || !params.spotName) {
      throw new Error('spotId and spotName are required for spot URLs');
    }

    const newQuery = { ...query };

    if (params.isTableView !== undefined) {
      if (params.isTableView) {
        newQuery.view = 'table';
      } else {
        delete newQuery.view;
      }
    }

    const finalQuery = updateCamIdInQuery(newQuery, params);

    return spotReportPath(finalQuery, {
      _id: params.spotId,
      name: params.spotName,
      isWavePool: params.isWavePool || false,
    });
  },
};

const buildNewUrl = (
  query: QueryObject,
  asPath: string,
  params: UpdateURLParams,
): string | null => {
  try {
    const { isCustomForecast, isWavePool, spotId, spotName } = params;

    if (isCustomForecast) {
      return URL_BUILDERS.customForecast(query, asPath, params);
    }
    if (isWavePool && spotId && spotName) {
      return URL_BUILDERS.wavePool(query, params);
    }
    if (spotId && spotName) {
      if (asPath?.includes('/rate-the-surf')) {
        return URL_BUILDERS.rateTheSurf(query, params);
      }
      return URL_BUILDERS.regular(query, params);
    }
  } catch (error) {
    console.warn('Failed to build URL:', error);
  }
  return null;
};

// Determines if table view should be set for new spots based on user preferences
const shouldSetTableViewForNewSpot = (
  currentTableView: boolean,
  isAnonymous: boolean,
  preferredForecastViewLocalStorage: string,
  preferredForecastView: string,
): boolean =>
  !currentTableView &&
  (isAnonymous ? preferredForecastViewLocalStorage === 'TABLE' : preferredForecastView === 'TABLE');

const safeRouterReplace = async (
  router: ReturnType<typeof useRouter>,
  url: string,
): Promise<boolean> => {
  try {
    await router.replace(url, undefined, { shallow: true, scroll: false });
    return true;
  } catch (error: any) {
    console.warn('Failed to update URL:', error?.message || error);
    return false;
  }
};

export const URLStateProvider: React.FC<URLStateProviderProps> = ({
  children,
  spotId,
  spotName,
  isWavePool = false,
}) => {
  const router = useRouter();
  const { query, asPath, isReady } = router;

  const userDetails = useAppSelector(getUserDetails);
  const isAnonymous = !userDetails;
  const preferredForecastView = useUserPreferredForecastView();
  const { preferredForecastViewLocalStorage } = usePreferredForecastView(isAnonymous);

  const [isHydrated, setIsHydrated] = useState(false);

  const currentSpotIdRef = useRef<string>('');
  const intendedUrlRef = useRef<string>('');
  const pendingUpdatesRef = useRef<Partial<UpdateURLParams>>({});
  const isNavigatingRef = useRef(false);
  const previousAsPathRef = useRef<string>('');

  const userPrefs = useMemo(
    () => ({
      isAnonymous,
      preferredForecastView: preferredForecastView || '',
      preferredForecastViewLocalStorage: preferredForecastViewLocalStorage || '',
    }),
    [isAnonymous, preferredForecastView, preferredForecastViewLocalStorage],
  );

  const debouncedProcessUpdates = useMemo(
    () =>
      debounce(
        async () => {
          const params = pendingUpdatesRef.current;
          pendingUpdatesRef.current = {};

          if (!isReady || !isHydrated || isNavigatingRef.current) {
            return;
          }

          // While contextual modals like Rate the Surf are open, avoid mutating the URL.
          // This preserves the masked path and internal query (so the modal stays open).
          const isRateTheSurfOpen =
            asPath?.includes('/rate-the-surf') || !!(query as any)?.rateTheSurf;
          if (isRateTheSurfOpen) {
            return;
          }

          const newUrl = buildNewUrl(query, asPath || '', params);

          if (!newUrl) {
            return;
          }

          const hashFragment = asPath?.includes('#') ? `#${asPath.split('#')[1]}` : '';
          const finalUrl = hashFragment ? `${newUrl}${hashFragment}` : newUrl;
          if (asPath !== finalUrl && intendedUrlRef.current !== finalUrl) {
            intendedUrlRef.current = finalUrl;
            await safeRouterReplace(router, finalUrl);
          }
        },
        5,
        { leading: false, trailing: true },
      ),
    [isReady, isHydrated, asPath, router, query],
  );

  const queueUpdate = useCallback(
    (updates: Partial<UpdateURLParams>) => {
      pendingUpdatesRef.current = {
        ...pendingUpdatesRef.current,
        ...updates,
      };

      debouncedProcessUpdates();
    },
    [debouncedProcessUpdates],
  );

  const handleSpotUpdates = useCallback(
    (urlSpotId: string, resolvedSpotName: string) => {
      if (isWavePool) {
        const hasUnwantedParams = query?.id || query?.slug || query?.view;

        if (hasUnwantedParams) {
          queueUpdate({
            spotId: urlSpotId,
            spotName: resolvedSpotName,
            isWavePool: true,
            isTableView: false,
          });
        }
        return;
      }

      // For regular spots, determine if table view should be active based on user preferences
      const currentTableView = query?.view === 'table';
      const shouldSetTableView = shouldSetTableViewForNewSpot(
        currentTableView,
        userPrefs.isAnonymous,
        userPrefs.preferredForecastViewLocalStorage,
        userPrefs.preferredForecastView,
      );

      if (shouldSetTableView) {
        queueUpdate({
          spotId: urlSpotId,
          spotName: resolvedSpotName,
          isWavePool,
          isTableView: true,
        });
      }
    },
    [isWavePool, userPrefs, queueUpdate, query],
  );

  // Memoized context functions to prevent unnecessary re-renders
  const updateViewPreference = useCallback(
    ({
      isTableView,
      spotId: paramSpotId,
      spotName: paramSpotName,
      isWavePool: paramIsWavePool = false,
    }: {
      isTableView: boolean;
      spotId?: string;
      spotName?: string;
      isWavePool?: boolean;
    }) => {
      // Priority: provided params > current URL query > provider props
      const finalSpotId = paramSpotId || (query.spotId as string) || spotId;
      const finalSpotName = paramSpotName || (query.spotSlug as string) || spotName;
      const finalIsWavePool = paramIsWavePool || isWavePool;

      // Determine if this is a custom forecast page
      const isCustomForecast = asPath?.includes('/custom-surf-forecast') ?? false;

      queueUpdate({
        isTableView,
        spotId: finalSpotId,
        spotName: finalSpotName,
        isWavePool: finalIsWavePool,
        isCustomForecast,
      });
    },
    [asPath, query.spotId, query.spotSlug, spotId, spotName, isWavePool, queueUpdate],
  );

  const updateCameraId = useCallback(
    ({
      camId,
      spotId: paramSpotId,
      spotName: paramSpotName,
      isWavePool: paramIsWavePool = false,
    }: {
      camId?: string;
      spotId?: string;
      spotName?: string;
      isWavePool?: boolean;
    }) => {
      // Priority: provided params > current URL query > provider props
      const finalSpotId = paramSpotId || (query.spotId as string) || spotId;
      const finalSpotName = paramSpotName || (query.spotSlug as string) || spotName;
      const finalIsWavePool = paramIsWavePool || isWavePool;

      // Determine if this is a custom forecast page
      const isCustomForecast = asPath?.includes('/custom-surf-forecast') ?? false;

      queueUpdate({
        camId,
        updateCamId: true, // Always update camId when this function is called
        spotId: finalSpotId,
        spotName: finalSpotName,
        isWavePool: finalIsWavePool,
        isCustomForecast,
      });
    },
    [asPath, query.spotId, query.spotSlug, spotId, spotName, isWavePool, queueUpdate],
  );

  useEffect(() => {
    // Detect if we're on a wave pool page
    const isWavePoolPage = asPath?.includes('/wave-pool');

    // Get the correct spot ID from query parameters based on page type
    const urlSpotId = isWavePoolPage ? (query.id as string) : (query.spotId as string);

    if (!isHydrated || !isReady || !urlSpotId) {
      return;
    }

    const isNewSpot = currentSpotIdRef.current !== urlSpotId;
    currentSpotIdRef.current = urlSpotId;

    // Check if this is an external URL change (browser navigation)
    const isExternalNavigation =
      previousAsPathRef.current &&
      asPath &&
      asPath !== previousAsPathRef.current &&
      asPath !== intendedUrlRef.current;
    if (!isNewSpot || isNavigatingRef.current || isExternalNavigation) {
      return;
    }

    const resolvedSpotName = spotName || (query.spotSlug as string) || '';
    if (resolvedSpotName) {
      handleSpotUpdates(urlSpotId, resolvedSpotName);
    }
  }, [
    query.spotId,
    query.id,
    query.spotSlug,
    spotName,
    isHydrated,
    isReady,
    asPath,
    handleSpotUpdates,
  ]);

  // Simplified router event handling
  useEffect(() => {
    // Update intended URL when router changes externally
    if (isReady && asPath) {
      intendedUrlRef.current = asPath;
      previousAsPathRef.current = asPath;
    }

    // Navigation event handlers
    const handleRouteChangeStart = () => {
      isNavigatingRef.current = true;
      pendingUpdatesRef.current = {};
      debouncedProcessUpdates.cancel();
    };

    const handleRouteChangeComplete = (url: string) => {
      isNavigatingRef.current = false;
      intendedUrlRef.current = url;
      previousAsPathRef.current = url;

      // Extract spotId from the new URL to update our spot tracking
      try {
        const urlObj = new URL(url, window.location.origin);
        const pathParts = urlObj.pathname.split('/');

        // Check if this is a wave pool page
        const isWavePoolPage = pathParts.includes('wave-pool');

        if (isWavePoolPage) {
          // For wave pools, extract ID from path (last segment)
          // URL structure: /wave-pool/[slug]/[id]
          const wavePoolId = pathParts[pathParts.length - 1];
          if (wavePoolId && wavePoolId !== 'wave-pool') {
            currentSpotIdRef.current = wavePoolId;
          }
        } else {
          // For regular spots, extract from path: /surf-report/[slug]/[spotId]/...
          const surfReportIndex = pathParts.findIndex((part) => part === 'surf-report');
          if (surfReportIndex >= 0 && pathParts.length > surfReportIndex + 2) {
            const candidateSpotId = pathParts[surfReportIndex + 2];
            if (candidateSpotId) {
              currentSpotIdRef.current = candidateSpotId;
            }
          }
        }
      } catch (error) {
        console.warn('Failed to parse URL for spot ID:', error);
      }
    };

    const handleRouteChangeError = () => {
      isNavigatingRef.current = false;
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeError', handleRouteChangeError);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.off('routeChangeError', handleRouteChangeError);
    };
  }, [isReady, asPath, router.events, debouncedProcessUpdates]);

  // Cleanup debounced function
  useEffect(
    () => () => {
      debouncedProcessUpdates.cancel();
    },
    [debouncedProcessUpdates],
  );

  useMount(() => {
    const timer = setTimeout(() => {
      setIsHydrated(true);
    }, 5);

    return () => clearTimeout(timer);
  });

  const value = useMemo(
    () => ({
      updateViewPreference,
      updateCameraId,
      isHydrated,
    }),
    [updateViewPreference, updateCameraId, isHydrated],
  );

  return <URLStateContext.Provider value={value}>{children}</URLStateContext.Provider>;
};
